import { apiFetch } from "../../core/utils/api";

const readResponseJsonSafe = async (response) => {
  try {
    return await response.json();
  } catch (_e) {
    return null;
  }
};

const buildApiError = async (response, fallbackMessage, dataOverride = undefined) => {
  // IMPORTANT: response bodies can only be read once. Many callers already parsed JSON, so allow passing it in.
  const data = dataOverride !== undefined ? dataOverride : await readResponseJsonSafe(response);
  const message = data?.error || data?.detail || data?.message || fallbackMessage;
  const err = new Error(message);
  err.status = response.status;
  err.data = data;

  // Keep admin UI auth state consistent: if backend says 401, force client to treat session as expired.
  // (Avoid importing React context here; use a DOM event.)
  if (response.status === 401 && typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    try {
      window.dispatchEvent(new CustomEvent("admin-auth-expired", { detail: { status: response.status, data } }));
    } catch (_e) {
      // ignore
    }
  }
  return err;
};

/**
 * Fetch overview statistics (total users, containers, websites, messages)
 * @param {string} timezone - Optional timezone name (default: 'America/New_York')
 * @returns {Promise<Object>} Overview stats object
 */
export const fetchOverviewStats = async (timezone = 'America/New_York') => {
  try {
    const params = new URLSearchParams();
    if (timezone) {
      params.append('timezone', timezone);
    }
    const url = `/api/admin/stats/overview/${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiFetch(url, {
      credentials: "include",
    });

    if (response.ok) {
      return await response.json();
    } else {
      throw new Error("Failed to fetch overview stats");
    }
  } catch (error) {
    console.error("Error fetching overview stats:", error);
    throw error;
  }
};

/**
 * Fetch list of all users
 * @returns {Promise<Array>} Array of user objects
 */
export const fetchUsersList = async () => {
  try {
    const response = await apiFetch("/api/admin/stats/users/", {
      credentials: "include",
    });

    if (response.ok) {
      return await response.json();
    } else {
      throw new Error("Failed to fetch users list");
    }
  } catch (error) {
    console.error("Error fetching users list:", error);
    throw error;
  }
};

/**
 * Fetch timeseries statistics for cumulative line chart
 * @param {string} startDate - Start date in YYYY-MM-DD format (optional)
 * @param {string} endDate - End date in YYYY-MM-DD format (optional)
 * @returns {Promise<Array>} Array of timeseries data points
 */
export const fetchTimeseriesStats = async (startDate = null, endDate = null) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);

    const url = `/api/admin/stats/timeseries/?${params.toString()}`;
    const response = await apiFetch(url, {
      credentials: "include",
    });

    if (response.ok) {
      return await response.json();
    } else {
      throw new Error("Failed to fetch timeseries stats");
    }
  } catch (error) {
    console.error("Error fetching timeseries stats:", error);
    throw error;
  }
};

/**
 * Fetch period statistics for bar charts
 * @param {string} entity - Entity type: 'user', 'container', or 'website'
 * @param {string} granularity - Time granularity: 'day', 'week', or 'month'
 * @param {string} startDate - Start date in YYYY-MM-DD format (optional)
 * @param {string} endDate - End date in YYYY-MM-DD format (optional)
 * @returns {Promise<Array>} Array of period data points
 */
export const fetchPeriodStats = async (
  entity,
  granularity,
  startDate = null,
  endDate = null
) => {
  try {
    const params = new URLSearchParams();
    params.append("entity", entity);
    params.append("granularity", granularity);
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);

    const url = `/api/admin/stats/period/?${params.toString()}`;
    const response = await apiFetch(url, {
      credentials: "include",
    });

    if (response.ok) {
      return await response.json();
    } else {
      throw new Error("Failed to fetch period stats");
    }
  } catch (error) {
    console.error("Error fetching period stats:", error);
    throw error;
  }
};

/**
 * Admin: list persisted template previews.
 */
export const fetchTemplatePreviews = async () => {
  const response = await apiFetch("/api/admin/template-previews/", {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load template previews", data);
};

/**
 * Admin: rebuild persisted template previews.
 */
export const rebuildTemplatePreviews = async (payload = {}) => {
  const response = await apiFetch("/api/admin/template-previews/rebuild/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to rebuild template previews", data);
};

/**
 * Admin: delete persisted template previews without recreating.
 */
export const deleteTemplatePreviews = async (payload = {}) => {
  const response = await apiFetch("/api/admin/template-previews/delete/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to delete template previews", data);
};

/**
 * Admin: delete a single template preview container by ID.
 */
export const deleteTemplatePreview = async (containerId) => {
  const response = await apiFetch(`/api/admin/template-previews/${containerId}/delete/`, {
    method: "DELETE",
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to delete template preview", data);
};

/**
 * Admin: screenshot template preview websites via Playwright (local dev only).
 * POST /api/admin/template-previews/screenshot/
 * Body: { domain?: string, action?: "delete" | "delete-all" }
 */
export const screenshotTemplatePreviews = async (domain) => {
  const body = domain ? { domain } : {};
  const response = await apiFetch("/api/admin/template-previews/screenshot/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, domain ? `Failed to screenshot "${domain}"` : "Failed to screenshot template previews", data);
};

/**
 * Admin: delete template preview screenshot files (local dev only).
 * POST /api/admin/template-previews/screenshot/
 * Body: { action: "delete-all" } or { action: "delete", domain: "..." }
 */
export const deleteTemplatePreviewScreenshots = async (domain) => {
  const body = domain ? { action: "delete", domain } : { action: "delete-all" };
  const response = await apiFetch("/api/admin/template-previews/screenshot/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, domain ? `Failed to delete screenshot for "${domain}"` : "Failed to delete template preview screenshots", data);
};

// ============================================================================
// Admin Landing Websites (curated list shown on public landing page)
// ============================================================================

/**
 * Admin: fetch current curated landing websites (featured containers list).
 * GET /api/admin/landing-websites/
 */
export const fetchLandingWebsites = async () => {
  const response = await apiFetch("/api/admin/landing-websites/", {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load landing websites", data);
};

/**
 * Admin: search website containers to feature on landing.
 * GET /api/admin/landing-websites/search/?name=&domain=&user=&limit=
 */
export const searchLandingWebsiteContainers = async ({ name = "", domain = "", user = "", limit = 50 } = {}) => {
  const params = new URLSearchParams();
  if (name) params.append("name", name);
  if (domain) params.append("domain", domain);
  if (user) params.append("user", user);
  if (limit) params.append("limit", String(limit));

  const url = params.toString()
    ? `/api/admin/landing-websites/search/?${params.toString()}`
    : "/api/admin/landing-websites/search/";

  const response = await apiFetch(url, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to search landing websites", data);
};

/**
 * Admin: save curated landing websites (ordered container ids).
 * POST /api/admin/landing-websites/
 * Body: { container_ids: number[] }
 */
export const saveLandingWebsites = async (containerIds = []) => {
  const response = await apiFetch("/api/admin/landing-websites/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ container_ids: containerIds || [] }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to save landing websites", data);
};

/**
 * Admin: fetch deterministic demo public websites available for landing selection.
 * GET /api/admin/landing-websites/demo-public-sites/
 */
export const fetchLandingDemoPublicSites = async () => {
  const response = await apiFetch("/api/admin/landing-websites/demo-public-sites/", {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load demo public websites", data);
};

/**
 * Admin: rebuild deterministic demo public websites from code.
 * POST /api/admin/landing-websites/demo-public-sites/
 */
export const rebuildLandingDemoPublicSites = async (payload = {}) => {
  const response = await apiFetch("/api/admin/landing-websites/demo-public-sites/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to rebuild demo public websites", data);
};

/**
 * Admin: rebuild a single deterministic demo public website by its spec key.
 * POST /api/admin/landing-websites/demo-public-sites/  { key: "<spec_key>" }
 */
export const rebuildSingleDemoPublicSite = async (key) => {
  const response = await apiFetch("/api/admin/landing-websites/demo-public-sites/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ key }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, `Failed to rebuild demo site "${key}"`, data);
};

/**
 * Admin: generate landing preview screenshots via the backend (local dev only).
 * POST /api/admin/landing-websites/screenshot-demos/
 * Body: { domain?: string }
 */
export const screenshotDemoSites = async (domain) => {
  const body = domain ? { domain } : {};
  const response = await apiFetch("/api/admin/landing-websites/screenshot-demos/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, domain ? `Failed to screenshot "${domain}"` : "Failed to screenshot demo sites", data);
};

// ============================================================================
// Admin Feedbacks
// ============================================================================

/**
 * Admin: list feedbacks (cursor pagination + filters).
 * GET /api/admin/feedbacks/
 */
export const fetchAdminFeedbacks = async ({
  cursor = null,
  limit = 50,
  user = "",
  user_id = "",
  rating = "",
  email = "",
  name = "",
  situation = "",
} = {}) => {
  const params = new URLSearchParams();
  if (cursor) params.append("cursor", String(cursor));
  if (limit) params.append("limit", String(limit));
  if (user) params.append("user", String(user));
  if (user_id) params.append("user_id", String(user_id));
  if (rating !== "" && rating != null) params.append("rating", String(rating));
  if (email) params.append("email", String(email));
  if (name) params.append("name", String(name));
  if (situation) params.append("situation", String(situation));

  const url = params.toString() ? `/api/admin/feedbacks/?${params.toString()}` : "/api/admin/feedbacks/";
  const response = await apiFetch(url, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load feedbacks", data);
};

// ============================================================================
// Admin Leads (PotentialCustomerLead)
// ============================================================================

/**
 * Admin: list leads (cursor pagination + filters).
 * GET /api/admin/leads/
 */
export const fetchAdminLeads = async ({
  cursor = null,
  limit = 50,
  user = "",
  user_id = "",
  email = "",
  name = "",
  celphone = "",
  situations = [],
  info = "",
} = {}) => {
  const params = new URLSearchParams();
  if (cursor) params.append("cursor", String(cursor));
  if (limit) params.append("limit", String(limit));
  if (user) params.append("user", String(user));
  if (user_id) params.append("user_id", String(user_id));
  if (email) params.append("email", String(email));
  if (name) params.append("name", String(name));
  if (celphone) params.append("celphone", String(celphone));
  if (info) params.append("info", String(info));

  const situationList = Array.isArray(situations) ? situations : [];
  situationList
    .map((s) => String(s || "").trim())
    .filter(Boolean)
    .forEach((s) => params.append("situation", s));

  const url = params.toString() ? `/api/admin/leads/?${params.toString()}` : "/api/admin/leads/";
  const response = await apiFetch(url, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load leads", data);
};

/**
 * Fetch detailed information for a specific user
 * @param {number} userId - User ID
 * @returns {Promise<Object>} User detail object
 */
export const fetchUserDetail = async (userId) => {
  try {
    const response = await apiFetch(`/api/admin/users/${userId}/`, {
      credentials: "include",
    });

    if (response.ok) {
      return await response.json();
    } else {
      throw new Error("Failed to fetch user detail");
    }
  } catch (error) {
    console.error("Error fetching user detail:", error);
    throw error;
  }
};

/**
 * Admin: change a user's subscription plan (Stripe-synced).
 * - If user already has a Stripe subscription, backend modifies it in Stripe and syncs local mirror.
 * - If user has no Stripe subscription, backend returns a Checkout URL that the admin can open/send to the user.
 */
export const adminChangeUserSubscription = async (userId, payload) => {
  const response = await apiFetch(`/api/admin/users/${userId}/subscription/change/`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });

  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to change user subscription");
};

/**
 * Admin: grant access (plan + duration) without payment.
 */
export const adminGrantUserAccess = async (userId, payload) => {
  const response = await apiFetch(`/api/admin/users/${userId}/subscription/access-grant/`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to grant access");
};

/**
 * Fetch coupons list (cursor pagination + filters)
 * @param {Object} params
 * @param {string|null} params.cursor
 * @param {number} params.limit - default 100, max 100
 * @param {string} params.q
 * @param {'active'|'inactive'|'all'} params.active_state
 */
export const fetchCoupons = async ({ cursor = null, limit = 100, q = "", active_state = "all" } = {}) => {
  try {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (active_state) params.append("active_state", active_state);
    if (limit) params.append("limit", String(limit));
    if (cursor) params.append("cursor", cursor);

    const url = params.toString() ? `/api/admin/coupons/?${params.toString()}` : "/api/admin/coupons/";
    const response = await apiFetch(url, {
      credentials: "include",
    });

    if (response.ok) {
      return await response.json();
    }

    throw await buildApiError(response, "Failed to fetch coupons");
  } catch (error) {
    console.error("Error fetching coupons:", error);
    throw error;
  }
};

/**
 * Create a coupon
 * Note: backend may return 500 with a created `coupon` when Stripe sync fails.
 */
export const createCoupon = async (payload) => {
  try {
    const response = await apiFetch("/api/admin/coupons/", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload || {}),
    });

    const data = await readResponseJsonSafe(response);
    if (response.ok) return data;

    // Partial success: coupon created but Stripe sync failed
    if (data?.coupon) return { ...data, warning: data?.error || "Coupon created but Stripe sync failed." };

    throw await buildApiError(response, "Failed to create coupon", data);
  } catch (error) {
    console.error("Error creating coupon:", error);
    throw error;
  }
};

/**
 * Bulk-create coupons with shared attributes and server-generated codes.
 * POST /api/admin/coupons/bulk/
 *
 * Partial success (some Stripe sync failures) still returns HTTP 201 with
 * `coupons`, `errors`, and `sync_warnings`.
 */
export const createCouponsBulk = async (payload) => {
  try {
    const response = await apiFetch("/api/admin/coupons/bulk/", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload || {}),
    });

    const data = await readResponseJsonSafe(response);
    if (response.ok) return data;

    throw await buildApiError(response, "Failed to create coupons", data);
  } catch (error) {
    console.error("Error creating coupons in bulk:", error);
    throw error;
  }
};

/**
 * Manually deactivate a coupon
 * Note: backend may return 500 with an updated `coupon` when Stripe deactivation fails.
 */
export const deactivateCoupon = async (couponId) => {
  try {
    const response = await apiFetch(`/api/admin/coupons/${couponId}/deactivate/`, {
      method: "POST",
      credentials: "include",
    });

    const data = await readResponseJsonSafe(response);
    if (response.ok) return data;

    // Partial success: coupon deactivated but Stripe deactivation failed
    if (data?.coupon) return { ...data, warning: data?.error || "Coupon deactivated but Stripe sync failed." };

    throw await buildApiError(response, "Failed to deactivate coupon", data);
  } catch (error) {
    console.error("Error deactivating coupon:", error);
    throw error;
  }
};

/**
 * Fetch the latest global discount (active or inactive). Returns null if none exists.
 */
export const fetchGlobalDiscount = async () => {
  const response = await apiFetch("/api/admin/global-discount/", {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch global discount");
};

/**
 * Create/update the global discount (single-discount upsert).
 * Note: backend may return 200/201 even if Stripe sync has warnings under `stripe`.
 */
export const upsertGlobalDiscount = async (payload) => {
  const response = await apiFetch("/api/admin/global-discount/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  if (data?.discount) return { ...data, warning: data?.error || "Global discount saved but Stripe sync failed." };
  throw await buildApiError(response, "Failed to save global discount");
};

/**
 * Deactivate current effective global discount.
 */
export const deactivateGlobalDiscount = async () => {
  const response = await apiFetch("/api/admin/global-discount/deactivate/", {
    method: "POST",
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  if (data?.discount) return { ...data, warning: data?.error || "Global discount deactivated but Stripe sync failed." };
  throw await buildApiError(response, "Failed to deactivate global discount");
};

/**
 * Fetch latest global access grant config (active or inactive). Returns null if none exists.
 */
export const fetchGlobalAccessGrant = async () => {
  const response = await apiFetch("/api/admin/global-access-grant/", {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch global access grant", data);
};

/**
 * Create/update the global access grant (single-grant upsert).
 */
export const upsertGlobalAccessGrant = async (payload) => {
  const response = await apiFetch("/api/admin/global-access-grant/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  if (data?.grant) return { ...data, warning: data?.error || "Global access grant saved with warnings." };
  throw await buildApiError(response, "Failed to save global access grant", data);
};

/**
 * Apply the global access grant to eligible users (chunked).
 */
export const applyGlobalAccessGrantBatch = async ({ cursor = null, limit = 200 } = {}) => {
  const response = await apiFetch("/api/admin/global-access-grant/apply/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cursor, limit }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to apply global access grant", data);
};

/**
 * Revoke the global access grant immediately (chunked).
 */
export const revokeGlobalAccessGrantBatch = async ({ cursor = null, limit = 200 } = {}) => {
  const response = await apiFetch("/api/admin/global-access-grant/revoke/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cursor, limit }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to revoke global access grant", data);
};

// ============================================================================
// Admin Subscription Config (platform-wide)
// ============================================================================

/**
 * Admin: fetch latest subscription config (or null if none).
 * GET /api/admin/subscription-config/
 */
export const fetchAdminSubscriptionConfig = async () => {
  const response = await apiFetch("/api/admin/subscription-config/", {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load subscription config", data);
};

/**
 * Admin: upsert latest subscription config (single-record pattern).
 * POST /api/admin/subscription-config/
 */
export const upsertAdminSubscriptionConfig = async (payload) => {
  const response = await apiFetch("/api/admin/subscription-config/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to save subscription config", data);
};

// NOTE: the affiliate endpoints below keep the legacy "influencer" segment in their
// URL paths (and the is_influencer / influencer_ids JSON keys) on purpose: the backend
// routes are not changing, so frontend and backend can deploy independently.

/**
 * Admin: list/search users for affiliate management.
 * GET /api/admin/influencers/users/
 */
export const fetchAffiliateUsers = async ({ q = "", only_affiliates = false, kind = "", kinds = [], limit = 50 } = {}) => {
  const params = new URLSearchParams();
  if (q) params.append("q", q);
  if (only_affiliates) params.append("only_influencers", "1");
  if (kind) params.append("kind", kind);
  // Account kinds to include ("user" | "affiliate" | "partner"); empty means all.
  if (Array.isArray(kinds) && kinds.length > 0) params.append("kinds", kinds.join(","));
  if (limit) params.append("limit", String(limit));

  const url = params.toString()
    ? `/api/admin/influencers/users/?${params.toString()}`
    : "/api/admin/influencers/users/";

  const response = await apiFetch(url, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch affiliate users", data);
};

/**
 * Admin: create a provisional affiliate account.
 * POST /api/admin/influencers/users/create/
 * Body: {
 *   username: string,
 *   email?: string,
 *   send_welcome_email?: boolean,
 *   commercial_kind?: "affiliate" | "partner" (backend defaults to "affiliate"),
 *   coupons?: Array<coupon create payload>,
 * }
 * Returns: {
 *   user: {...list row shape, must_complete_onboarding: true},
 *   initial_password: string,
 *   welcome_email_sent: boolean,
 *   welcome_email_error: string|null,
 *   coupons: Array,
 *   coupon_sync_warnings: string[],
 * }
 */
export const createAffiliateUser = async ({
  username,
  email = "",
  send_welcome_email = false,
  commercial_kind = "",
  coupons,
  default_coupon = null,
  default_access = null,
  access_grant = null,
} = {}) => {
  const body = { username };
  if (email) body.email = email;
  if (send_welcome_email) body.send_welcome_email = true;
  if (commercial_kind) body.commercial_kind = commercial_kind;
  if (Array.isArray(coupons) && coupons.length > 0) body.coupons = coupons;
  if (default_coupon) body.default_coupon = default_coupon;
  if (default_access) body.default_access = default_access;
  if (access_grant) body.access_grant = access_grant;
  const response = await apiFetch("/api/admin/influencers/users/create/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to create affiliate account", data);
};

/**
 * Admin: regenerate the initial password for a provisional affiliate account.
 * POST /api/admin/influencers/users/<id>/regenerate-password/
 * Returns: { initial_password: string } (400 if the user already completed onboarding)
 */
export const regenerateAffiliateUserPassword = async (userId) => {
  const response = await apiFetch(`/api/admin/influencers/users/${userId}/regenerate-password/`, {
    method: "POST",
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to regenerate password", data);
};

/**
 * Admin: send the single appropriate welcome email for an affiliate.
 * POST /api/admin/influencers/users/<id>/send-welcome/
 *
 * - Pending first access → credentials email (username + temp password + coupons).
 *   The stored password is only a hash, so it is regenerated; any password shared
 *   earlier stops working. Returns initial_password once.
 * - Already onboarded → explanation email (program overview + coupons); no credentials.
 *
 * Returns: { welcome_email_sent: true, email_kind: "credentials"|"welcome", initial_password?: string }
 * (400 if not an affiliate or the account has only a placeholder email)
 */
export const sendAffiliateWelcomeEmail = async (userId) => {
  const response = await apiFetch(`/api/admin/influencers/users/${userId}/send-welcome/`, {
    method: "POST",
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to send welcome email", data);
};

/**
 * @deprecated Prefer sendAffiliateWelcomeEmail. Legacy pending-only credentials endpoint.
 */
export const sendAffiliateUserCredentials = async (userId) => {
  const response = await apiFetch(`/api/admin/influencers/users/${userId}/send-credentials/`, {
    method: "POST",
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to send credentials email", data);
};

/**
 * Admin: set a user's affiliate flag.
 * POST /api/admin/influencers/users/<id>/set/
 */
export const setUserAffiliateFlag = async (userId, is_affiliate) => {
  const response = await apiFetch(`/api/admin/influencers/users/${userId}/set/`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ is_influencer: !!is_affiliate }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to update affiliate flag", data);
};

/**
 * Admin: set a user's account kind ("user" | "affiliate" | "partner").
 * POST /api/admin/influencers/users/<id>/commercial-kind/
 * Body: { commercial_kind: "user" | "affiliate" | "partner" } → { user: {...} } (400 on bad value)
 *
 * "user" drops the portal role entirely; the other two grant it. The wire key
 * keeps its `commercial_kind` name — the deployed route is unchanged.
 */
export const setUserCommercialKind = async (userId, commercialKind) => {
  const response = await apiFetch(`/api/admin/influencers/users/${userId}/commercial-kind/`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ commercial_kind: commercialKind }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to change account kind", data);
};

// ============================================================================
// Admin Coupon Pack Pricing (volume-discount tiers for self-serve Buy Coupons)
// ============================================================================

/**
 * Admin: fetch the global default coupon-pack pricing tiers.
 * GET /api/admin/coupon-pack-pricing/default/
 * Returns: { tiers: [{min_quantity, percent_off}, ...], override_user_rules: boolean }
 */
export const fetchCouponPackPricingDefault = async () => {
  const response = await apiFetch("/api/admin/coupon-pack-pricing/default/", {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load default pack pricing", data);
};

/**
 * Admin: save the global default coupon-pack pricing tiers.
 * PUT /api/admin/coupon-pack-pricing/default/
 * Body/Returns: { tiers: [...], override_user_rules: boolean }
 */
export const saveCouponPackPricingDefault = async ({ tiers = [], override_user_rules = false } = {}) => {
  const response = await apiFetch("/api/admin/coupon-pack-pricing/default/", {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tiers, override_user_rules }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to save default pack pricing", data);
};

/**
 * Admin: fetch a user's coupon-pack pricing (override tiers or a copy of the default).
 * GET /api/admin/coupon-pack-pricing/users/<id>/
 * Returns: { has_override: bool, tiers: [...], default_tiers: [...], override_user_rules: bool }
 */
export const fetchUserCouponPackPricing = async (userId) => {
  const response = await apiFetch(`/api/admin/coupon-pack-pricing/users/${userId}/`, {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load user pack pricing", data);
};

/**
 * Admin: save a user's coupon-pack pricing override (fully replaces the default for that user).
 * PUT /api/admin/coupon-pack-pricing/users/<id>/
 * Body: { tiers: [...] } → { has_override: true, tiers: [...] }
 */
export const saveUserCouponPackPricing = async (userId, tiers = []) => {
  const response = await apiFetch(`/api/admin/coupon-pack-pricing/users/${userId}/`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tiers }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to save user pack pricing", data);
};

/**
 * Admin: remove a user's coupon-pack pricing override (falls back to the default).
 * DELETE /api/admin/coupon-pack-pricing/users/<id>/
 * Returns: { has_override: false, tiers: [default tiers] }
 */
export const deleteUserCouponPackPricing = async (userId) => {
  const response = await apiFetch(`/api/admin/coupon-pack-pricing/users/${userId}/`, {
    method: "DELETE",
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to remove user pack pricing override", data);
};

/**
 * Admin: update affiliate assignments for a coupon.
 * POST /api/admin/coupons/<id>/influencers/
 */
export const setCouponAffiliates = async (couponId, affiliate_ids = []) => {
  const response = await apiFetch(`/api/admin/coupons/${couponId}/influencers/`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ influencer_ids: affiliate_ids || [] }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to update coupon affiliates", data);
};

/**
 * Admin: list coupons linked to a specific affiliate user.
 * GET /api/admin/influencers/users/<id>/coupons/
 */
export const fetchAffiliateUserCoupons = async (userId) => {
  const response = await apiFetch(`/api/admin/influencers/users/${userId}/coupons/`, {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch affiliate coupons", data);
};

/**
 * Admin: list every version (published + draft) of a website container.
 * GET /api/admin/containers/<container_id>/versions/
 */
export const fetchAdminContainerVersions = async (containerId) => {
  const response = await apiFetch(`/api/admin/containers/${containerId}/versions/`, {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch website versions", data);
};

/**
 * Admin: set which coupon a given affiliate promotes.
 * POST /api/admin/influencers/users/<id>/promoted-coupon/
 */
export const setAffiliatePromotedCoupon = async (userId, couponId) => {
  const response = await apiFetch(`/api/admin/influencers/users/${userId}/promoted-coupon/`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ coupon_id: couponId ?? null }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to set promoted coupon", data);
};

// ============================================================================
// Admin Coupon Defaults (global templates for affiliate/partner coupons)
// ============================================================================

/**
 * Admin: fetch a global coupon default template.
 * GET /api/admin/coupon-defaults/<kind>/  (kind: "regular" | "access")
 * Returns: { configured: boolean, template: {...}|null }
 * - regular template: coupon fields (percent_off/amount_off/currency, include_renewals,
 *   free_trial_days, max_redemptions, first_time_only, plans, billing_periods,
 *   free-access fields) plus `suffix` (digits-only string, may be empty).
 * - access template: free-access pair, max_redemptions (required), first_time_only,
 *   plus `default_count` (how many access codes a batch creates by default).
 */
export const fetchCouponDefault = async (kind) => {
  const response = await apiFetch(`/api/admin/coupon-defaults/${kind}/`, {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load coupon default", data);
};

/**
 * Admin: save a global coupon default template.
 * PUT /api/admin/coupon-defaults/<kind>/  Body: the template object.
 * Returns: { configured: true, template: {...} }
 */
export const saveCouponDefault = async (kind, template) => {
  const response = await apiFetch(`/api/admin/coupon-defaults/${kind}/`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(template || {}),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to save coupon default", data);
};

/**
 * Admin: apply the global default coupon and/or a default access batch to an
 * existing affiliate/partner user.
 * POST /api/admin/influencers/users/<id>/apply-defaults/
 * Body: { default_coupon: { code }?, default_access: { count }? } (at least one)
 * Returns: { coupons: [...created coupons...], sync_warnings: [...] }
 */
export const applyUserCouponDefaults = async (userId, { default_coupon = null, default_access = null } = {}) => {
  const body = {};
  if (default_coupon) body.default_coupon = default_coupon;
  if (default_access) body.default_access = default_access;
  const response = await apiFetch(`/api/admin/influencers/users/${userId}/apply-defaults/`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to apply coupon defaults", data);
};

// ============================================================================
// Admin Broadcasts
// ============================================================================

/**
 * Admin: preview recipients for a broadcast (max 30 sample).
 * POST /api/admin/broadcasts/recipients/preview/
 */
export const adminBroadcastRecipientsPreview = async (payload) => {
  const response = await apiFetch("/api/admin/broadcasts/recipients/preview/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to preview recipients", data);
};

/**
 * Admin: count recipients for a broadcast.
 * POST /api/admin/broadcasts/recipients/count/
 */
export const adminBroadcastRecipientsCount = async (payload) => {
  const response = await apiFetch("/api/admin/broadcasts/recipients/count/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to count recipients", data);
};

/**
 * Admin: fetch broadcast filter metadata (plan names, subscription statuses, etc).
 * GET /api/admin/broadcasts/filters/meta/
 */
export const adminBroadcastFiltersMeta = async () => {
  const response = await apiFetch("/api/admin/broadcasts/filters/meta/", {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load broadcast filter metadata", data);
};

// ============================================================================
// Admin Account Deletion Requests
// ============================================================================

/**
 * Admin: list all pending account deletion requests.
 * GET /api/admin/account-deletions/
 */
export const fetchAccountDeletions = async () => {
  const response = await apiFetch("/api/admin/account-deletions/", {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load account deletion requests", data);
};

/**
 * Admin: cancel a specific pending deletion request.
 * POST /api/admin/account-deletions/<deletionId>/cancel/
 */
export const adminCancelDeletionRequest = async (deletionId) => {
  const response = await apiFetch(`/api/admin/account-deletions/${deletionId}/cancel/`, {
    method: "POST",
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to cancel deletion request", data);
};

/**
 * Admin: permanently execute (delete) a user account for a pending deletion request.
 * POST /api/admin/account-deletions/<deletionId>/execute/
 */
export const adminExecuteDeletionRequest = async (deletionId) => {
  const response = await apiFetch(`/api/admin/account-deletions/${deletionId}/execute/`, {
    method: "POST",
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to execute deletion request", data);
};

/**
 * Admin: send a broadcast (chunked delivery).
 * POST /api/admin/broadcasts/send/
 */
export const adminBroadcastSend = async (payload) => {
  const response = await apiFetch("/api/admin/broadcasts/send/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to send broadcast", data);
};

// ============================================================================
// Admin Automations (scheduled, condition-driven messages)
// ============================================================================

const AUTOMATIONS_BASE = "/api/admin/automations/";

const jsonRequestOptions = (method, payload) => ({
  method,
  credentials: "include",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(payload || {}),
});

/**
 * Admin: list automations (summaries with last run + message counts).
 * GET /api/admin/automations/?state=draft|active|paused
 * @returns {Promise<{results: Array}>}
 */
export const adminAutomationsList = async ({ state = "" } = {}) => {
  const params = new URLSearchParams();
  if (state) params.set("state", state);
  const qs = params.toString();
  const response = await apiFetch(`${AUTOMATIONS_BASE}${qs ? `?${qs}` : ""}`, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load automations", data);
};

/**
 * Admin: create an automation (always starts as draft unless the payload says otherwise).
 * POST /api/admin/automations/
 * @returns {Promise<{automation: Object}>}
 */
export const adminAutomationCreate = async (payload) => {
  const response = await apiFetch(AUTOMATIONS_BASE, jsonRequestOptions("POST", payload));
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to create automation", data);
};

/**
 * Admin: automation detail (summary + recent_runs + membership_counts).
 * GET /api/admin/automations/<id>/
 * @returns {Promise<{automation: Object}>}
 */
export const adminAutomationGet = async (id) => {
  const response = await apiFetch(`${AUTOMATIONS_BASE}${id}/`, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load automation", data);
};

/**
 * Admin: partial update — the payload is merged over the stored values and validated as a whole.
 * PATCH /api/admin/automations/<id>/
 * @returns {Promise<{automation: Object}>}
 */
export const adminAutomationUpdate = async (id, payload) => {
  const response = await apiFetch(`${AUTOMATIONS_BASE}${id}/`, jsonRequestOptions("PATCH", payload));
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to update automation", data);
};

/**
 * Admin: delete an automation (message history is kept).
 * DELETE /api/admin/automations/<id>/ → 204
 * @returns {Promise<boolean>}
 */
export const adminAutomationDelete = async (id) => {
  const response = await apiFetch(`${AUTOMATIONS_BASE}${id}/`, { method: "DELETE", credentials: "include" });
  if (response.ok) return true;
  throw await buildApiError(response, "Failed to delete automation");
};

/**
 * Admin: change an automation's state (draft | active | paused).
 * POST /api/admin/automations/<id>/state/
 * @returns {Promise<{automation: Object}>}
 */
export const adminAutomationSetState = async (id, state) => {
  const response = await apiFetch(`${AUTOMATIONS_BASE}${id}/state/`, jsonRequestOptions("POST", { state }));
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to change automation state", data);
};

/**
 * Admin: row-free live preview of an automation payload (include `id` when editing a saved one).
 * POST /api/admin/automations/preview/ → funnel counts, policy_reasons, sample_users
 * 400 {error} on a validation problem.
 */
export const adminAutomationPreview = async (payload) => {
  const response = await apiFetch(`${AUTOMATIONS_BASE}preview/`, jsonRequestOptions("POST", payload));
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to preview automation", data);
};

/**
 * Admin: render the message variables of a title/body for a real sample user.
 * POST /api/admin/automations/render-preview/ {title, body_markdown, user_id?}
 * @returns {Promise<{title: string, body_markdown: string, sample_user: Object|null, variables: Object, warnings: string[]}>}
 */
export const adminAutomationRenderPreview = async (payload) => {
  const response = await apiFetch(`${AUTOMATIONS_BASE}render-preview/`, jsonRequestOptions("POST", payload));
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to render the message preview", data);
};

/**
 * Admin: run an automation now (drafts get a recorded dry run; active/paused get a real run).
 * POST /api/admin/automations/<id>/run/
 * @returns {Promise<{run: Object}>}
 */
export const adminAutomationRunNow = async (id) => {
  const response = await apiFetch(`${AUTOMATIONS_BASE}${id}/run/`, { method: "POST", credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to run automation", data);
};

/**
 * Admin: run history for one automation (newest first).
 * GET /api/admin/automations/<id>/runs/?limit=
 * @returns {Promise<{results: Array}>}
 */
export const adminAutomationRuns = async (id, { limit = 20 } = {}) => {
  const params = new URLSearchParams();
  if (limit) params.set("limit", String(limit));
  const qs = params.toString();
  const response = await apiFetch(`${AUTOMATIONS_BASE}${id}/runs/${qs ? `?${qs}` : ""}`, {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load automation runs", data);
};

/**
 * Admin: run history across EVERY automation (newest first), each row carrying
 * its automation {id,name,slug} and affected_user_count (distinct users, not
 * message count). Powers the "Run history" section on the Automations page.
 * GET /api/admin/automations/runs/?automation_id&automation_slug&status&dry_run&page&page_size
 * @returns {Promise<{results: Array, page: number, page_size: number, total: number, has_more: boolean}>}
 */
export const adminAutomationRunsAll = async (params = {}) => {
  const response = await apiFetch(`${AUTOMATIONS_BASE}runs/${buildQueryString(params)}`, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load run history", data);
};

/**
 * Admin: editor metadata — situations, guards (with params schemas), automation slugs,
 * email template variants, default sender name, state + trigger choices.
 * GET /api/admin/automations/conditions/
 */
export const adminAutomationConditions = async () => {
  const response = await apiFetch(`${AUTOMATIONS_BASE}conditions/`, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load automation conditions", data);
};

/**
 * Admin: platform-wide delivery policy (frequency caps + quiet hours).
 * GET /api/admin/automations/settings/
 * @returns {Promise<{settings: Object}>}
 */
export const adminAutomationSettingsGet = async () => {
  const response = await apiFetch(`${AUTOMATIONS_BASE}settings/`, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load automation settings", data);
};

/**
 * Admin: save the delivery policy (any subset of fields; omitted ones keep their value).
 * POST /api/admin/automations/settings/
 * @returns {Promise<{settings: Object}>}
 */
export const adminAutomationSettingsUpdate = async (payload) => {
  const response = await apiFetch(`${AUTOMATIONS_BASE}settings/`, jsonRequestOptions("POST", payload));
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to save automation settings", data);
};

// ============================================================================
// Admin Messages (every message / email the platform sent or has scheduled)
// ============================================================================

const MESSAGES_BASE = "/api/admin/messages/";
const EMAIL_SUPPRESSIONS_BASE = "/api/admin/email-suppressions/";

/**
 * Query string from a params object. Empty strings, null, undefined and empty
 * arrays are skipped; arrays are comma-joined (the API's list filters);
 * booleans become "true" / "false". Returns "" or "?a=b&c=d".
 */
const buildQueryString = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value)) {
      const joined = value
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
        .map((v) => String(v).trim())
        .join(",");
      if (joined) search.set(key, joined);
      return;
    }
    if (typeof value === "boolean") {
      search.set(key, value ? "true" : "false");
      return;
    }
    const text = String(value).trim();
    if (text) search.set(key, text);
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

/**
 * Admin: page of outbound messages (automations, broadcasts, system emails).
 * GET /api/admin/messages/?page&page_size&bucket&status&q&recipient_kind&recipient_was_paid
 *     &origin&origin_detail&automation_id&automation_slug&channel&email_template_variant
 *     &created_from&created_to&scheduled_from&scheduled_to&ordering
 * @returns {Promise<{results: Array, page: number, page_size: number, total: number, has_more: boolean, counts: Object}>}
 *   `counts` (per status) ignore status/bucket so the tab badges reflect the current search.
 */
export const adminMessagesList = async (params = {}) => {
  const response = await apiFetch(`${MESSAGES_BASE}${buildQueryString(params)}`, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load messages", data);
};

/**
 * Admin: one message in full (list row + body_markdown, metadata and, for
 * email rows, rendered_html — the branded template output).
 * GET /api/admin/messages/<id>/
 * @returns {Promise<{message: Object}>}
 */
export const adminMessageGet = async (id) => {
  const response = await apiFetch(`${MESSAGES_BASE}${id}/`, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load message", data);
};

/**
 * Admin: cancel a still-scheduled message.
 * POST /api/admin/messages/<id>/cancel/ → 200 {message} | 409 {error} when no longer scheduled.
 * @returns {Promise<{message: Object}>}
 */
export const adminMessageCancel = async (id) => {
  const response = await apiFetch(`${MESSAGES_BASE}${id}/cancel/`, { method: "POST", credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to cancel message", data);
};

/**
 * Admin: choice lists for the Messages filter bar (statuses, origins,
 * origin_details, recipient_kinds, channels, email_template_variants,
 * automations, buckets).
 * GET /api/admin/messages/filters/meta/
 */
export const adminMessagesFiltersMeta = async () => {
  const response = await apiFetch(`${MESSAGES_BASE}filters/meta/`, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load message filters", data);
};

/**
 * Admin: the do-not-email list, newest first.
 * GET /api/admin/email-suppressions/?q&page&page_size
 * @returns {Promise<{results: Array, total: number, page: number, page_size: number}>}
 */
export const adminEmailSuppressionsList = async (params = {}) => {
  const response = await apiFetch(`${EMAIL_SUPPRESSIONS_BASE}${buildQueryString(params)}`, {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load email suppressions", data);
};

/**
 * Admin: add an address to the do-not-email list (manual / admin entry).
 * POST /api/admin/email-suppressions/ {email, notes?} → 201 {suppression}
 * @returns {Promise<{suppression: Object}>}
 */
export const adminEmailSuppressionAdd = async (payload) => {
  const response = await apiFetch(EMAIL_SUPPRESSIONS_BASE, jsonRequestOptions("POST", payload));
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to add email suppression", data);
};

/**
 * Admin: remove an address from the do-not-email list.
 * DELETE /api/admin/email-suppressions/<id>/ → 204
 * @returns {Promise<boolean>}
 */
export const adminEmailSuppressionDelete = async (id) => {
  const response = await apiFetch(`${EMAIL_SUPPRESSIONS_BASE}${id}/`, { method: "DELETE", credentials: "include" });
  if (response.ok) return true;
  throw await buildApiError(response, "Failed to remove email suppression");
};

// ============================================================================
// Admin Blog Management
// ============================================================================

/**
 * Admin: list all blog posts (published + unpublished).
 * GET /api/admin/blog/
 */
export const fetchAdminBlogPosts = async () => {
  const response = await apiFetch("/api/admin/blog/", { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load blog posts", data);
};

/**
 * Admin: create a new blog post.
 * POST /api/admin/blog/
 */
export const createBlogPost = async (payload) => {
  const response = await apiFetch("/api/admin/blog/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to create blog post", data);
};

/**
 * Admin: update an existing blog post.
 * PATCH /api/admin/blog/<id>/update/
 */
export const updateBlogPost = async (postId, payload) => {
  const response = await apiFetch(`/api/admin/blog/${postId}/update/`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to update blog post", data);
};

/**
 * Admin: publish a blog post.
 * POST /api/admin/blog/<id>/publish/
 */
export const publishBlogPost = async (postId) => {
  const response = await apiFetch(`/api/admin/blog/${postId}/publish/`, {
    method: "POST",
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to publish blog post", data);
};

/**
 * Admin: unpublish a blog post.
 * POST /api/admin/blog/<id>/unpublish/
 */
export const unpublishBlogPost = async (postId) => {
  const response = await apiFetch(`/api/admin/blog/${postId}/unpublish/`, {
    method: "POST",
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to unpublish blog post", data);
};

/**
 * Admin: delete a blog post.
 * DELETE /api/admin/blog/<id>/delete/
 */
export const deleteBlogPost = async (postId) => {
  const response = await apiFetch(`/api/admin/blog/${postId}/delete/`, {
    method: "DELETE",
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to delete blog post", data);
};

/**
 * Admin: get hidden static article slugs.
 * GET /api/admin/blog/hidden-static/
 */
export const fetchHiddenStaticArticles = async () => {
  const response = await apiFetch("/api/admin/blog/hidden-static/", { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to load hidden static articles", data);
};

/**
 * Admin: hide a static article.
 * POST /api/admin/blog/hidden-static/
 */
export const hideStaticArticle = async (slug) => {
  const response = await apiFetch("/api/admin/blog/hidden-static/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slug }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to hide static article", data);
};

/**
 * Admin: unhide a static article.
 * DELETE /api/admin/blog/hidden-static/
 */
export const unhideStaticArticle = async (slug) => {
  const response = await apiFetch("/api/admin/blog/hidden-static/", {
    method: "DELETE",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slug }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to unhide static article", data);
};

/**
 * Admin: list affiliate commission periods (payout review queue).
 * GET /api/admin/affiliate-payouts/?status=&search=&limit=&offset=
 * Returns: {
 *   periods: [{...period-json, affiliate: {id, username, email},
 *              account: {exists, is_payout_ready}}],
 *   status_counts: {"<status>": int, ...},  // over the UNFILTERED queryset
 *   total_count: int,
 * }
 */
export const fetchAdminAffiliatePayouts = async ({ status = "", search = "", limit = 25, offset = 0 } = {}) => {
  const params = new URLSearchParams();
  if (status) params.append("status", status);
  if (search) params.append("search", search);
  if (limit) params.append("limit", String(limit));
  if (offset) params.append("offset", String(offset));

  const url = params.toString()
    ? `/api/admin/affiliate-payouts/?${params.toString()}`
    : "/api/admin/affiliate-payouts/";

  const response = await apiFetch(url, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch affiliate payouts", data);
};

/**
 * Admin: full status-event history of one commission period.
 * GET /api/admin/affiliate-payouts/<id>/events/
 * Returns: { events: [{from_status, to_status, actor_type, note, created_at, ...}] }
 */
export const fetchAdminAffiliatePayoutEvents = async (periodId) => {
  const response = await apiFetch(`/api/admin/affiliate-payouts/${periodId}/events/`, {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch payout history", data);
};

/**
 * Admin: approve a commission period and execute the Stripe payout.
 * POST /api/admin/affiliate-payouts/<id>/approve/
 * Allowed from on_hold, pending_approval, failed (retry).
 * 409 when the affiliate's payout account is not ready.
 */
export const approveAdminAffiliatePayout = async (periodId) => {
  const response = await apiFetch(`/api/admin/affiliate-payouts/${periodId}/approve/`, {
    method: "POST",
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to approve payout", data);
};

/**
 * Admin: reject a commission period with a note shown to the affiliate.
 * POST /api/admin/affiliate-payouts/<id>/reject/  Body: { note }
 * Note is required non-empty (400 otherwise); allowed from on_hold,
 * pending_approval, pending_setup.
 */
export const rejectAdminAffiliatePayout = async (periodId, note) => {
  const response = await apiFetch(`/api/admin/affiliate-payouts/${periodId}/reject/`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ note }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to reject payout", data);
};
